import { useCallback } from 'react';
import { toast } from 'sonner';

import { Seed } from '@/types';

/**
 * Seed share action handlers
 *
 * Provides handlers for sharing seed data via clipboard.
 * Export modal state is now managed by useSeedModal hook.
 */
export interface SeedShareHandlers {
  /** Share seed URLs by copying to clipboard */
  handleShare: () => void;
}

/**
 * Hook for seed share handlers
 *
 * Provides share functionality for seeds by generating deep links with drawer state.
 * Export modal state is now managed by useSeedModal hook for consistency.
 *
 * @param selectedSeeds - Currently selected seeds
 * @returns Share handler
 *
 * @example
 * ```tsx
 * const { handleShare } = useSeedShareHandlers(selectedSeeds);
 *
 * return (
 *   <ShareButton onShareClick={handleShare} />
 * );
 * ```
 */
export const useSeedShareHandlers = (
  selectedSeeds: Seed[]
): SeedShareHandlers => {
  /**
   * Shares seed URLs by copying them to clipboard
   * Generates deep links with drawer state for direct access
   */
  const handleShare = useCallback(() => {
    try {
      const urls = selectedSeeds.map(seed => {
        // Extract base path up to userId (e.g., "/encoded-user-id")
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const basePath = pathParts[0] ? `/${pathParts[0]}` : '';

        const params = new URLSearchParams();
        params.append('seedDrawerKey', seed.key);
        params.append('drawerOrder', JSON.stringify(['seed']));
        return `${window.location.origin}${basePath}/seeds?${params.toString()}`;
      });

      navigator.clipboard.writeText(urls.join('\n'));

      toast.success(
        `Copied ${urls.length} seed URL${urls.length > 1 ? 's' : ''} to clipboard`
      );
    } catch (error) {
      toast.error('Failed to copy URLs to clipboard');
      console.error('[useSeedShareHandlers] Failed to share:', error);
    }
  }, [selectedSeeds]);

  return {
    handleShare,
  };
};
